/**
 * Reusable Client Profile Modal Component
 * Can be used across all admin sections (Build, Protect, Grow, Finance, etc.)
 * 
 * Usage:
 * import ClientProfileModal from '../../components/admin/ClientProfileModal';
 * 
 * <ClientProfileModal 
 *   client={selectedClient} 
 *   onClose={() => setSelectedClient(null)} 
 *   isModal={true}  // true for overlay modal, false for full page
 * />
 */

import { useState } from 'react';
import { 
  User, Mail, Phone, Building, FileText, Truck, Shield, 
  BarChart3, Package, X, MapPin, Calendar, Hash, DollarSign,
  Star, Clock, Activity, TrendingUp, Award, FolderOpen
} from 'lucide-react';
import ClientDocumentVault from './ClientDocumentVault';

// Package/Tier color mapping
const PACKAGE_COLORS = {
  Bronze: '#CD7F32',
  Silver: '#C0C0C0',
  Gold: '#D4AF37',
  Basic: '#CD7F32',
  Professional: '#C0C0C0',
  Elite: '#D4AF37',
  Foundation: '#CD7F32',
  Accelerator: '#C0C0C0',
  default: '#D4AF37'
};

const getPackageColor = (packageName) => {
  return PACKAGE_COLORS[packageName] || PACKAGE_COLORS.default;
};

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    delivered: { bg: 'bg-green-500/20', text: 'text-green-400' },
    in_transit: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
    active: { bg: 'bg-green-500/20', text: 'text-green-400' },
    inactive: { bg: 'bg-red-500/20', text: 'text-red-400' },
    default: { bg: 'bg-gray-500/20', text: 'text-gray-400' }
  };
  
  const config = statusConfig[status] || statusConfig.default;
  const displayText = status?.replace('_', ' ') || 'Unknown';
  
  return (
    <span className={`px-2 py-1 text-xs ${config.bg} ${config.text}`}>
      {displayText}
    </span>
  );
};

// Client Profile Modal/Page Component
const ClientProfileModal = ({ client, onClose, isModal = false }) => {
  if (!client) return null;
  
  const pkgColor = getPackageColor(client.package);

  // Modal overlay wrapper
  if (isModal) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 z-50 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        data-testid="client-profile-modal-overlay"
      >
        <div className="min-h-screen py-8 px-4">
          <div className="max-w-6xl mx-auto bg-[#0A0A0A] border border-white/10 relative">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors z-10"
              data-testid="client-profile-close-btn"
            >
              <X className="w-6 h-6" />
            </button>
            
            <ClientProfileContent client={client} pkgColor={pkgColor} />
          </div>
        </div>
      </div>
    );
  }

  // Full page version (non-modal)
  return <ClientProfileContent client={client} pkgColor={pkgColor} />;
};

// The actual profile content
const ClientProfileContent = ({ client, pkgColor }) => {
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'documents', label: 'Documents', icon: FolderOpen }
  ];

  return (
    <div className="p-6" data-testid="client-profile-content">
      {/* Profile Header */}
      <div 
        className="bg-[#121212] border border-white/10 p-6 mb-6" 
        style={{ borderTopWidth: '4px', borderTopColor: pkgColor }}
        data-testid="client-profile-header"
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div 
              className="w-16 h-16 rounded flex items-center justify-center text-2xl font-bold" 
              style={{ backgroundColor: `${pkgColor}20`, color: pkgColor }}
            >
              {client.company?.charAt(0) || 'C'}
            </div>
            <div>
              <h2 className="text-white text-2xl font-bold" data-testid="client-company-name">
                {client.company}
              </h2>
              <p className="text-white/50">{client.address}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {client.package && (
                  <span 
                    className="px-2 py-1 text-xs font-bold" 
                    style={{ backgroundColor: `${pkgColor}20`, color: pkgColor }}
                    data-testid="client-package-badge"
                  >
                    {client.package} Package
                  </span>
                )}
                {client.joined && (
                  <span className="text-white/40 text-sm flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Client since {client.joined}
                  </span>
                )}
              </div>
            </div>
          </div>
          {client.id && (
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm">ID:</span>
              <span className="text-white font-mono" data-testid="client-id">{client.id}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-[#121212] border border-white/10 p-1 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white/10 text-white' 
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`client-tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <ProfileTabContent client={client} pkgColor={pkgColor} />
      )}

      {activeTab === 'documents' && (
        <ClientDocumentVault client={client} isAdmin={true} pkgColor={pkgColor} />
      )}
    </div>
  );
};

// Profile tab content (original profile view)
const ProfileTabContent = ({ client, pkgColor }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Contact Info */}
          {client.contact && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-contact-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" style={{ color: pkgColor }} />
                Contact Information
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-white font-medium">{client.contact.name}</p>
                  <p className="text-white/50 text-sm">{client.contact.title}</p>
                </div>
                {client.contact.email && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Mail className="w-4 h-4 text-white/40" />
                    {client.contact.email}
                  </div>
                )}
                {client.contact.phone && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Phone className="w-4 h-4 text-white/40" />
                    {client.contact.phone}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Authority Info */}
          {(client.dotNumber || client.mcNumber) && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-authority-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: pkgColor }} />
                Authority Information
              </h3>
              <div className="space-y-3">
                {client.dotNumber && (
                  <div className="flex justify-between">
                    <span className="text-white/50">DOT Number</span>
                    <span className="text-white font-mono">{client.dotNumber}</span>
                  </div>
                )}
                {client.mcNumber && (
                  <div className="flex justify-between">
                    <span className="text-white/50">MC Number</span>
                    <span className="text-white font-mono">{client.mcNumber}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fleet Info */}
          {client.fleet && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-fleet-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5" style={{ color: pkgColor }} />
                Fleet Information
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-[#0A0A0A] border border-white/5 p-3">
                  <p className="text-2xl font-bold" style={{ color: pkgColor }}>{client.fleet.trucks}</p>
                  <p className="text-white/40 text-xs">Trucks</p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/5 p-3">
                  <p className="text-2xl font-bold" style={{ color: pkgColor }}>{client.fleet.trailers}</p>
                  <p className="text-white/40 text-xs">Trailers</p>
                </div>
                <div className="bg-[#0A0A0A] border border-white/5 p-3">
                  <p className="text-2xl font-bold" style={{ color: pkgColor }}>{client.fleet.drivers}</p>
                  <p className="text-white/40 text-xs">Drivers</p>
                </div>
              </div>
            </div>
          )}

          {/* Insurance */}
          {client.insurance && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-insurance-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" style={{ color: pkgColor }} />
                Insurance
              </h3>
              <div className="space-y-2">
                {client.insurance.provider && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Provider</span>
                    <span className="text-white">{client.insurance.provider}</span>
                  </div>
                )}
                {client.insurance.coverage && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Coverage</span>
                    <span className="text-white">{client.insurance.coverage}</span>
                  </div>
                )}
                {client.insurance.expiry && (
                  <div className="flex justify-between">
                    <span className="text-white/50">Expires</span>
                    <span className="text-white">{client.insurance.expiry}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Stats & History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Performance Stats */}
          {client.stats && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-performance-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: pkgColor }} />
                Performance Metrics
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {client.stats.totalLoads !== undefined && (
                  <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center">
                    <p className="text-3xl font-bold" style={{ color: pkgColor }}>{client.stats.totalLoads}</p>
                    <p className="text-white/40 text-xs">Total Loads</p>
                  </div>
                )}
                {client.stats.onTimeDelivery !== undefined && (
                  <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center">
                    <p className="text-3xl font-bold text-[#10B981]">{client.stats.onTimeDelivery}%</p>
                    <p className="text-white/40 text-xs">On-Time</p>
                  </div>
                )}
                {client.stats.avgRating !== undefined && (
                  <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center">
                    <p className="text-3xl font-bold text-[#D4AF37]">{client.stats.avgRating}</p>
                    <p className="text-white/40 text-xs">Avg Rating</p>
                  </div>
                )}
                {client.stats.totalRevenue !== undefined && (
                  <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center">
                    <p className="text-3xl font-bold text-[#22C55E]">${(client.stats.totalRevenue / 1000).toFixed(0)}k</p>
                    <p className="text-white/40 text-xs">Total Revenue</p>
                  </div>
                )}
                {client.stats.avgRatePerMile !== undefined && (
                  <div className="bg-[#0A0A0A] border border-white/5 p-4 text-center">
                    <p className="text-3xl font-bold text-white">${client.stats.avgRatePerMile}</p>
                    <p className="text-white/40 text-xs">Avg $/Mile</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Load History */}
          {client.loadHistory && client.loadHistory.length > 0 && (
            <div className="bg-[#121212] border border-white/10 overflow-hidden" data-testid="client-load-history-section">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Package className="w-5 h-5" style={{ color: pkgColor }} />
                  Recent Load History
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/50 text-xs uppercase p-3">Load ID</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Route</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Date</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Rate</th>
                      <th className="text-left text-white/50 text-xs uppercase p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {client.loadHistory.map((load) => (
                      <tr key={load.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 font-mono" style={{ color: pkgColor }}>{load.id}</td>
                        <td className="p-3 text-white">{load.route}</td>
                        <td className="p-3 text-white/70">{load.date}</td>
                        <td className="p-3 text-[#22C55E] font-bold">${load.rate?.toLocaleString()}</td>
                        <td className="p-3">
                          <StatusBadge status={load.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {client.notes && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-notes-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: pkgColor }} />
                Notes
              </h3>
              <p className="text-white/70">{client.notes}</p>
            </div>
          )}

          {/* Additional Info (for flexible data) */}
          {client.additionalInfo && Object.keys(client.additionalInfo).length > 0 && (
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="client-additional-info-section">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" style={{ color: pkgColor }} />
                Additional Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(client.additionalInfo).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-white/50 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-white">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

// Export helper function for consistent color usage
export { getPackageColor, StatusBadge };
export default ClientProfileModal;
